import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp, TrendingDown, Download, Loader2, IndianRupee,
  BarChart3, ArrowUpRight, ArrowDownLeft, Calendar, FileSpreadsheet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FeatureGate } from "../components/FeatureGate";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (a) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

function getCurrentFY() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month < 4) return `${year - 1}-${String(year).slice(2)}`;
  return `${year}-${String(year + 1).slice(2)}`;
}

function getFYOptions() {
  const now = new Date();
  const options = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
    options.push(`${y}-${String(y + 1).slice(2)}`);
  }
  return options;
}

const TABS = [
  { id: "turnover", label: "Turnover Summary" },
  { id: "sales", label: "Sales Register" },
  { id: "purchase", label: "Purchase Register" },
];

export default function FinancialReportsPage() {
  const { defaultCompanyId } = useAuth();
  const [tab, setTab] = useState("turnover");
  const [fy, setFy] = useState(getCurrentFY());
  const [loading, setLoading] = useState(false);
  const [turnover, setTurnover] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);

  useEffect(() => { fetchData(); }, [tab, fy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { fy };
      if (defaultCompanyId) params.company_id = defaultCompanyId;

      if (tab === "turnover") {
        const res = await axios.get(`${API}/reports/turnover`, { params, withCredentials: true });
        setTurnover(res.data);
      } else if (tab === "sales") {
        const res = await axios.get(`${API}/reports/sales-register`, { params, withCredentials: true });
        setSalesData(res.data);
      } else if (tab === "purchase") {
        const res = await axios.get(`${API}/reports/purchase-register`, { params, withCredentials: true });
        setPurchaseData(res.data);
      }
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (type) => {
    const params = new URLSearchParams({ fy });
    if (defaultCompanyId) params.set("company_id", defaultCompanyId);
    window.open(`${API}/reports/export/${type}?${params.toString()}`, "_blank");
  };

  const maxBar = turnover ? Math.max(...turnover.monthly.map(m => Math.max(m.sales, m.purchases)), 1) : 1;

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="financial-reports-page">
      <FeatureGate feature="analytics">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#2563EB]" /> Financial Reports
            </h1>
            <p className="text-sm text-[#6B7280] mt-1">Sales, Purchase registers and Turnover summary</p>
          </div>
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-36" data-testid="fy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {getFYOptions().map(o => <SelectItem key={o} value={o}>FY {o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto" data-testid="report-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-[#2563EB] text-white" : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"
              }`}
            >{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" /></div>
        ) : (
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            {/* TURNOVER TAB */}
            {tab === "turnover" && turnover && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-[#10B981] mb-1"><ArrowUpRight className="w-4 h-4" /><span className="text-xs font-medium uppercase tracking-wider">Total Sales</span></div>
                    <p className="text-xl font-bold text-[#111827]" data-testid="total-sales">{fmt(turnover.totals.total_sales)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-[#EF4444] mb-1"><ArrowDownLeft className="w-4 h-4" /><span className="text-xs font-medium uppercase tracking-wider">Total Purchases</span></div>
                    <p className="text-xl font-bold text-[#111827]" data-testid="total-purchases">{fmt(turnover.totals.total_purchases)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-[#2563EB] mb-1"><TrendingUp className="w-4 h-4" /><span className="text-xs font-medium uppercase tracking-wider">Net Turnover</span></div>
                    <p className="text-xl font-bold text-[#111827]" data-testid="net-turnover">{fmt(turnover.totals.net_turnover)}</p>
                  </div>
                </div>

                {/* Monthly Chart */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-[#111827] mb-4">Monthly Breakdown</h3>
                  <div className="space-y-3">
                    {turnover.monthly.map(m => {
                      const label = new Date(m.month + "-15").toLocaleString("en-IN", { month: "short", year: "2-digit" });
                      return (
                        <div key={m.month} className="flex items-center gap-3">
                          <span className="text-xs text-[#6B7280] w-14 text-right flex-shrink-0">{label}</span>
                          <div className="flex-1 flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className="h-4 rounded-r bg-[#10B981]/20 border-l-4 border-[#10B981]" style={{ width: `${Math.max((m.sales / maxBar) * 100, 0.5)}%` }} />
                              <span className="text-xs text-[#6B7280] flex-shrink-0">{fmt(m.sales)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="h-4 rounded-r bg-[#EF4444]/20 border-l-4 border-[#EF4444]" style={{ width: `${Math.max((m.purchases / maxBar) * 100, 0.5)}%` }} />
                              <span className="text-xs text-[#6B7280] flex-shrink-0">{fmt(m.purchases)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-4 text-xs text-[#6B7280]">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#10B981]" /> Sales</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#EF4444]" /> Purchases</span>
                  </div>
                </div>

                {/* Quarterly */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-[#111827] mb-3">Quarterly Summary</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-[#F9FAFB]">
                        <th className="text-left px-3 py-2 text-xs font-medium text-[#6B7280]">Quarter</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Sales</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Purchases</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Net</th>
                      </tr></thead>
                      <tbody>
                        {turnover.quarterly.map(q => (
                          <tr key={q.quarter} className="border-t border-[#F3F4F6]">
                            <td className="px-3 py-2 font-medium">{q.quarter}</td>
                            <td className="px-3 py-2 text-right text-[#10B981]">{fmt(q.sales)}</td>
                            <td className="px-3 py-2 text-right text-[#EF4444]">{fmt(q.purchases)}</td>
                            <td className="px-3 py-2 text-right font-medium">{fmt(q.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SALES REGISTER TAB */}
            {tab === "sales" && salesData && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-[#6B7280]">{salesData.count} invoice(s)</p>
                  <Button variant="outline" size="sm" onClick={() => handleExport("sales-register")} data-testid="export-sales-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Export Excel
                  </Button>
                </div>
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="sales-register-table">
                      <thead><tr className="bg-[#F9FAFB]">
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Invoice</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Date</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Customer</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">Taxable</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">CGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">SGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">IGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">Total</th>
                        <th className="text-center px-3 py-2.5 text-xs font-medium text-[#6B7280]">Status</th>
                      </tr></thead>
                      <tbody>
                        {salesData.entries.map(e => (
                          <tr key={e.invoice_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                            <td className="px-3 py-2.5 font-medium text-[#2563EB]">{e.invoice_number}</td>
                            <td className="px-3 py-2.5 text-[#6B7280]">{e.invoice_date}</td>
                            <td className="px-3 py-2.5 text-[#111827]">{e.customer_name}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(e.taxable_value)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.cgst)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.sgst)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.igst)}</td>
                            <td className="px-3 py-2.5 text-right font-medium">{fmt(e.grand_total)}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${e.payment_status === "paid" ? "bg-[#D1FAE5] text-[#059669]" : "bg-[#FEF3C7] text-[#D97706]"}`}>
                                {e.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {salesData.totals && (
                        <tfoot>
                          <tr className="bg-[#F9FAFB] font-semibold border-t-2 border-[#E5E7EB]">
                            <td colSpan={3} className="px-3 py-2.5">Total</td>
                            <td className="px-3 py-2.5 text-right">{fmt(salesData.totals.taxable_value)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(salesData.totals.cgst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(salesData.totals.sgst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(salesData.totals.igst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(salesData.totals.grand_total)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* PURCHASE REGISTER TAB */}
            {tab === "purchase" && purchaseData && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-[#6B7280]">{purchaseData.count} invoice(s)</p>
                  <Button variant="outline" size="sm" onClick={() => handleExport("purchase-register")} data-testid="export-purchase-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Export Excel
                  </Button>
                </div>
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="purchase-register-table">
                      <thead><tr className="bg-[#F9FAFB]">
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Invoice</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Date</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-[#6B7280]">Vendor</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">Taxable</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">CGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">SGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">IGST</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-[#6B7280]">Total</th>
                        <th className="text-center px-3 py-2.5 text-xs font-medium text-[#6B7280]">Status</th>
                      </tr></thead>
                      <tbody>
                        {purchaseData.entries.map(e => (
                          <tr key={e.invoice_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                            <td className="px-3 py-2.5 font-medium text-[#2563EB]">{e.invoice_number}</td>
                            <td className="px-3 py-2.5 text-[#6B7280]">{e.invoice_date}</td>
                            <td className="px-3 py-2.5 text-[#111827]">{e.vendor_name}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(e.taxable_value)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.cgst)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.sgst)}</td>
                            <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(e.igst)}</td>
                            <td className="px-3 py-2.5 text-right font-medium">{fmt(e.grand_total)}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${e.payment_status === "paid" ? "bg-[#D1FAE5] text-[#059669]" : "bg-[#FEF3C7] text-[#D97706]"}`}>
                                {e.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {purchaseData.totals && (
                        <tfoot>
                          <tr className="bg-[#F9FAFB] font-semibold border-t-2 border-[#E5E7EB]">
                            <td colSpan={3} className="px-3 py-2.5">Total</td>
                            <td className="px-3 py-2.5 text-right">{fmt(purchaseData.totals.taxable_value)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(purchaseData.totals.cgst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(purchaseData.totals.sgst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(purchaseData.totals.igst)}</td>
                            <td className="px-3 py-2.5 text-right">{fmt(purchaseData.totals.grand_total)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Empty state */}
            {((tab === "sales" && salesData?.count === 0) || (tab === "purchase" && purchaseData?.count === 0)) && (
              <div className="text-center py-16 bg-white rounded-xl border border-[#E5E7EB]" data-testid="empty-state">
                <FileSpreadsheet className="w-12 h-12 text-[#D1D5DB] mx-auto mb-3" />
                <p className="text-[#374151] font-medium">No {tab === "sales" ? "sales" : "purchase"} invoices found</p>
                <p className="text-sm text-[#6B7280] mt-1">for FY {fy}</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
      </FeatureGate>
    </div>
  );
}
